import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { speakText } from '../utils/audioUtils';
import BottomNav from '../components/BottomNav';
import LessonBannerAd from '../components/LessonBannerAd';
import { fetchGrammarByTopic, fetchGrammarTopicByType } from '../api/grammarApi';
import '../screens/GrammarScreen.css';

interface GrammarExample {
  id: string;
  italian: string;
  bengali: string;
  english: string;
  explanation?: string;
}

interface VerbConjugation {
  person: string;
  personBn: string;
  form: string;
  formBn: string;
}

interface VerbData {
  id: string;
  infinitive: string;
  infinitiveBn: string;
  english?: string;
  conjugations: VerbConjugation[];
}

const GrammarDetailScreen: React.FC = () => {
  const navigate = useNavigate();
  const { type } = useParams<{ type: string }>();
  const { currentLanguage } = useLanguage();
  const [selectedExample, setSelectedExample] = useState<string | null>(null);
  const [fetchedData, setFetchedData] = useState<{ rules: { italian: string; bengali: string }[]; examples: GrammarExample[] | VerbData[] } | null>(null);
  const [topicTitle, setTopicTitle] = useState<{ title_it: string; title_bn: string; title_en: string } | null>(null);
  const hasSpokenRef = useRef(false);

  useEffect(() => {
    if (!type) return;
    let cancelled = false;
    fetchGrammarByTopic(type).then((data) => {
      if (!cancelled && data && (data.rules.length > 0 || data.examples.length > 0)) {
        setFetchedData({
          rules: data.rules,
          examples: data.examples as GrammarExample[] | VerbData[],
        });
      } else {
        setFetchedData(null);
      }
    });
    return () => { cancelled = true; };
  }, [type]);

  useEffect(() => {
    if (!type) return;
    fetchGrammarTopicByType(type).then((t) => setTopicTitle(t));
  }, [type]);

  // Language content
  const content = {
    bn: {
      verbTenses: "ক্রিয়া কাল",
      articles: "আর্টিকেল",
      pronouns: "সর্বনাম",
      prepositions: "অব্যয়",
      adjectives: "বিশেষণ",
      questions: "প্রশ্ন",
      negation: "নিষেধ",
      verbs: "ক্রিয়া",
      back: "ফিরে যান",
      rules: "নিয়ম",
      examples: "উদাহরণ",
      tapToSpeak: "শোনার জন্য আলতো চাপুন"
    },
    it: {
      verbTenses: "Tempi Verbali",
      articles: "Articoli",
      pronouns: "Pronomi",
      prepositions: "Preposizioni",
      adjectives: "Aggettivi",
      questions: "Domande",
      negation: "Negazione",
      verbs: "Verbi",
      back: "Indietro",
      rules: "Regole",
      examples: "Esempi",
      tapToSpeak: "Tocca per sentire"
    },
    en: {
      verbTenses: "Verb Tenses",
      articles: "Articles",
      pronouns: "Pronouns",
      prepositions: "Prepositions",
      adjectives: "Adjectives",
      questions: "Questions",
      negation: "Negation",
      verbs: "Verbs",
      back: "Back",
      rules: "Rules",
      examples: "Examples",
      tapToSpeak: "Tap to hear"
    }
  };

  // Grammar rules and examples data
  const grammarData: { [key: string]: { rules: { italian: string; bengali: string }[]; examples: GrammarExample[] | VerbData[] } } = {
    'verb-tenses': {
      rules: [
        { italian: 'Presente: Usa per azioni attuali', bengali: 'বর্তমান: বর্তমান কাজের জন্য ব্যবহার করুন' },
        { italian: 'Passato Prossimo: Usa per azioni completate', bengali: 'অতীত: সম্পন্ন কাজের জন্য ব্যবহার করুন' },
        { italian: 'Futuro: Usa per azioni future', bengali: 'ভবিষ্যৎ: ভবিষ্যতের কাজের জন্য ব্যবহার করুন' }
      ],
      examples: [
        { id: '1', italian: 'Io mangio', bengali: 'আমি খাই', english: 'I eat' },
        { id: '2', italian: 'Ho mangiato', bengali: 'আমি খেয়েছি', english: 'I ate' },
        { id: '3', italian: 'Mangerò', bengali: 'আমি খাব', english: 'I will eat' },
        { id: '4', italian: 'Tu parli', bengali: 'তুমি বলো', english: 'You speak' },
        { id: '5', italian: 'Hai parlato', bengali: 'তুমি বলেছ', english: 'You spoke' },
        { id: '6', italian: 'Parlerai', bengali: 'তুমি বলবে', english: 'You will speak' },
        { id: '7', italian: 'Lui studia', bengali: 'সে পড়ে', english: 'He studies' },
        { id: '8', italian: 'Ha studiato', bengali: 'সে পড়েছে', english: 'He studied' },
        { id: '9', italian: 'Studierà', bengali: 'সে পড়বে', english: 'He will study' }
      ]
    },
    'articles': {
      rules: [
        { italian: 'Articoli Determinativi: il (masc. sing.), la (fem. sing.), i (masc. pl.), le (fem. pl.)', bengali: 'নির্দিষ্ট আর্টিকেল: il (পুরুষ একবচন), la (স্ত্রী একবচন), i (পুরুষ বহুবচন), le (স্ত্রী বহুবচন)' },
        { italian: 'Articoli Indeterminativi: un (masc.), una (fem.)', bengali: 'অনির্দিষ্ট আর্টিকেল: un (পুরুষ), una (স্ত্রী)' },
        { italian: 'Gli articoli devono concordare con genere e numero del sostantivo', bengali: 'আর্টিকেলগুলি বিশেষ্যের লিঙ্গ এবং সংখ্যার সাথে মিলতে হবে' }
      ],
      examples: [
        { id: '1', italian: 'il libro', bengali: 'বই', english: 'the book' },
        { id: '2', italian: 'la casa', bengali: 'বাড়ি', english: 'the house' },
        { id: '3', italian: 'un gatto', bengali: 'একটি বিড়াল', english: 'a cat' },
        { id: '4', italian: 'una mela', bengali: 'একটি আপেল', english: 'an apple' },
        { id: '5', italian: 'i libri', bengali: 'বইগুলো', english: 'the books' },
        { id: '6', italian: 'le case', bengali: 'বাড়িগুলো', english: 'the houses' },
        { id: '7', italian: 'il cane', bengali: 'কুকুর', english: 'the dog' },
        { id: '8', italian: 'la strada', bengali: 'রাস্তা', english: 'the road' }
      ]
    },
    'pronouns': {
      rules: [
        { italian: 'Pronomi Soggetto: io (আমি), tu (তুমি), lui/lei (সে), noi (আমরা), voi (তোমরা), loro (তারা)', bengali: 'কর্তৃকারক সর্বনাম: io (আমি), tu (তুমি), lui/lei (সে), noi (আমরা), voi (তোমরা), loro (তারা)' },
        { italian: 'Pronomi Oggetto Diretto: mi, ti, lo, la, ci, vi, li, le', bengali: 'সরাসরি কর্মকারক সর্বনাম: mi, ti, lo, la, ci, vi, li, le' },
        { italian: 'Pronomi Oggetto Indiretto: mi, ti, gli, le, ci, vi, gli', bengali: 'পরোক্ষ কর্মকারক সর্বনাম: mi, ti, gli, le, ci, vi, gli' }
      ],
      examples: [
        { id: '1', italian: 'Io sono italiano', bengali: 'আমি ইতালীয়', english: 'I am Italian' },
        { id: '2', italian: 'Tu sei bengalese', bengali: 'তুমি বাঙালি', english: 'You are Bengali' },
        { id: '3', italian: 'Lui studia', bengali: 'সে পড়ে', english: 'He studies' },
        { id: '4', italian: 'Lei lavora', bengali: 'সে কাজ করে', english: 'She works' },
        { id: '5', italian: 'Noi andiamo', bengali: 'আমরা যাই', english: 'We go' },
        { id: '6', italian: 'Voi parlate', bengali: 'তোমরা বলো', english: 'You speak' },
        { id: '7', italian: 'Loro mangiano', bengali: 'তারা খায়', english: 'They eat' },
        { id: '8', italian: 'Mi piace', bengali: 'আমার ভালো লাগে', english: 'I like' }
      ]
    },
    'prepositions': {
      rules: [
        { italian: 'Le preposizioni indicano luogo, direzione o tempo', bengali: 'অব্যয়গুলি স্থান, দিক বা সময় নির্দেশ করে' },
        { italian: 'Preposizioni comuni: di (এর), a (এ), da (থেকে), in (এ), con (সাথে), su (উপর), per (জন্য)', bengali: 'সাধারণ অব্যয়: di (এর), a (এ), da (থেকে), in (এ), con (সাথে), su (উপর), per (জন্য)' },
        { italian: 'Alcune preposizioni si combinano con articoli: del, della, dei, delle', bengali: 'কিছু অব্যয় আর্টিকেলের সাথে মিলিত হয়: del, della, dei, delle' }
      ],
      examples: [
        { id: '1', italian: 'Vado a scuola', bengali: 'আমি স্কুলে যাই', english: 'I go to school' },
        { id: '2', italian: 'Vengo da casa', bengali: 'আমি বাড়ি থেকে আসি', english: 'I come from home' },
        { id: '3', italian: 'Sono in Italia', bengali: 'আমি ইতালিতে', english: 'I am in Italy' },
        { id: '4', italian: 'Il libro è su tavolo', bengali: 'বইটি টেবিলের উপর আছে', english: 'The book is on the table' },
        { id: '5', italian: 'Vado con amico', bengali: 'আমি এক বন্ধুর সাথে যাই', english: 'I go with friend' },
        { id: '6', italian: 'Studio per esame', bengali: 'আমি পরীক্ষার জন্য পড়ি', english: 'I study for exam' },
        { id: '7', italian: 'Pane di grano', bengali: 'গমের রুটি', english: 'Bread of wheat' },
        { id: '8', italian: 'Alla stazione', bengali: 'স্টেশনে', english: 'At the station' }
      ]
    },
    'adjectives': {
      rules: [
        { italian: 'Gli aggettivi devono concordare con il sostantivo in genere e numero', bengali: 'বিশেষণগুলি বিশেষ্যের লিঙ্গ এবং সংখ্যার সাথে মিলতে হবে' },
        { italian: 'La maggior parte degli aggettivi: -o (পুরুষ একবচন), -a (স্ত্রী একবচন), -i (পুরুষ বহুবচন), -e (স্ত্রী বহুবচন)', bengali: 'বেশিরভাগ বিশেষণ: -o (পুরুষ একবচন), -a (স্ত্রী একবচন), -i (পুরুষ বহুবচন), -e (স্ত্রী বহুবচন)' },
        { italian: 'Gli aggettivi di solito vengono dopo il sostantivo, ma alcuni vengono prima', bengali: 'বিশেষণগুলি সাধারণত বিশেষ্যের পরে আসে, তবে কিছু আগে আসে' }
      ],
      examples: [
        { id: '1', italian: 'libro rosso', bengali: 'লাল বই', english: 'red book' },
        { id: '2', italian: 'casa rossa', bengali: 'লাল বাড়ি', english: 'red house' },
        { id: '3', italian: 'libri rossi', bengali: 'লাল বইগুলো', english: 'red books' },
        { id: '4', italian: 'case rosse', bengali: 'লাল বাড়িগুলো', english: 'red houses' },
        { id: '5', italian: 'uomo alto', bengali: 'লম্বা মানুষ', english: 'tall man' },
        { id: '6', italian: 'donna alta', bengali: 'লম্বা নারী', english: 'tall woman' },
        { id: '7', italian: 'bella casa', bengali: 'সুন্দর বাড়ি', english: 'beautiful house' },
        { id: '8', italian: 'buon cibo', bengali: 'ভালো খাবার', english: 'good food' }
      ]
    },
    'questions': {
      rules: [
        { italian: 'Parole interrogative: Chi (কে), Cosa/Che (কী), Dove (কোথায়), Quando (কখন), Perché (কেন), Come (কেমন)', bengali: 'প্রশ্নবাচক শব্দ: Chi (কে), Cosa/Che (কী), Dove (কোথায়), Quando (কখন), Perché (কেন), Come (কেমন)' },
        { italian: 'Per fare una domanda, alza la voce alla fine o usa parole interrogative', bengali: 'প্রশ্ন করতে, শেষে কণ্ঠস্বর বাড়ান বা প্রশ্নবাচক শব্দ ব্যবহার করুন' },
        { italian: 'Semplici domande sì/no possono essere formate con intonazione', bengali: 'সাধারণ হ্যাঁ/না প্রশ্ন স্বরভঙ্গি দিয়ে তৈরি করা যায়' }
      ],
      examples: [
        { id: '1', italian: 'Chi sei?', bengali: 'তুমি কে?', english: 'Who are you?' },
        { id: '2', italian: 'Cosa fai?', bengali: 'তুমি কী করো?', english: 'What do you do?' },
        { id: '3', italian: 'Dove vai?', bengali: 'তুমি কোথায় যাচ্ছ?', english: 'Where are you going?' },
        { id: '4', italian: 'Quando arrivi?', bengali: 'তুমি কখন আসবে?', english: 'When will you arrive?' },
        { id: '5', italian: 'Perché piangi?', bengali: 'তুমি কাঁদছ কেন?', english: 'Why are you crying?' },
        { id: '6', italian: 'Come stai?', bengali: 'তুমি কেমন আছ?', english: 'How are you?' },
        { id: '7', italian: 'Parli italiano?', bengali: 'তুমি কি ইতালীয় বলো?', english: 'Do you speak Italian?' },
        { id: '8', italian: 'Hai fame?', bengali: 'তোমার কি ক্ষুধা?', english: 'Are you hungry?' }
      ]
    },
    'negation': {
      rules: [
        { italian: 'Usa "non" prima del verbo per fare frasi negative', bengali: 'নেতিবাচক বাক্য তৈরি করতে ক্রিয়ার আগে "non" ব্যবহার করুন' },
        { italian: 'Non significa "না" e si colloca direttamente prima del verbo', bengali: 'Non মানে "না" এবং ক্রিয়ার ঠিক আগে রাখা হয়' },
        { italian: 'Le doppie negazioni sono comuni e corrette in italiano', bengali: 'ইতালীয় ভাষায় দ্বৈত নেতিবাচকতা সাধারণ এবং সঠিক' }
      ],
      examples: [
        { id: '1', italian: 'Non parlo', bengali: 'আমি বলি না', english: 'I do not speak' },
        { id: '2', italian: 'Non mangio', bengali: 'আমি খাই না', english: 'I do not eat' },
        { id: '3', italian: 'Non vado', bengali: 'আমি যাই না', english: 'I do not go' },
        { id: '4', italian: 'Non è qui', bengali: 'সে এখানে নেই', english: 'He is not here' },
        { id: '5', italian: 'Non ho fame', bengali: 'আমার ক্ষুধা নেই', english: 'I am not hungry' },
        { id: '6', italian: 'Non capisco', bengali: 'আমি বুঝি না', english: 'I do not understand' },
        { id: '7', italian: 'Non vuole', bengali: 'সে চায় না', english: 'He does not want' },
        { id: '8', italian: 'Non posso', bengali: 'আমি পারি না', english: 'I cannot' }
      ]
    },
    'verbs': {
      rules: [
        { italian: 'Presente: Usa per azioni attuali o abitudini', bengali: 'বর্তমান: বর্তমান কাজ বা অভ্যাসের জন্য ব্যবহার করুন' },
        { italian: 'Io (আমি), Tu (তুমি), Lui/Lei (সে), Noi (আমরা), Voi (তোমরা), Loro (তারা)', bengali: 'Io (আমি), Tu (তুমি), Lui/Lei (সে), Noi (আমরা), Voi (তোমরা), Loro (তারা)' },
        { italian: 'Tocca ogni forma per ascoltare la pronuncia', bengali: 'উচ্চারণ শোনার জন্য প্রতিটি ফর্মে আলতো চাপুন' }
      ],
      examples: [
        {
          id: '1',
          infinitive: 'Avere',
          infinitiveBn: 'থাকা',
          english: 'to have',
          conjugations: [
            { person: 'io', personBn: 'আমার', form: 'ho', formBn: 'আছে' },
            { person: 'tu', personBn: 'তোমার', form: 'hai', formBn: 'আছে' },
            { person: 'lui/lei', personBn: 'তার', form: 'ha', formBn: 'আছে' },
            { person: 'noi', personBn: 'আমাদের', form: 'abbiamo', formBn: 'আছে' },
            { person: 'voi', personBn: 'তোমাদের', form: 'avete', formBn: 'আছে' },
            { person: 'loro', personBn: 'তাদের', form: 'hanno', formBn: 'আছে' }
          ]
        },
        {
          id: '2',
          infinitive: 'Essere',
          infinitiveBn: 'হওয়া',
          english: 'to be',
          conjugations: [
            { person: 'io', personBn: 'আমি', form: 'sono', formBn: 'হই' },
            { person: 'tu', personBn: 'তুমি', form: 'sei', formBn: 'হও' },
            { person: 'lui/lei', personBn: 'সে', form: 'è', formBn: 'হয়' },
            { person: 'noi', personBn: 'আমরা', form: 'siamo', formBn: 'হই' },
            { person: 'voi', personBn: 'তোমরা', form: 'siete', formBn: 'হও' },
            { person: 'loro', personBn: 'তারা', form: 'sono', formBn: 'হয়' }
          ]
        },
        {
          id: '3',
          infinitive: 'Fare',
          infinitiveBn: 'করা',
          english: 'to do/make',
          conjugations: [
            { person: 'io', personBn: 'আমি', form: 'faccio', formBn: 'করি' },
            { person: 'tu', personBn: 'তুমি', form: 'fai', formBn: 'করো' },
            { person: 'lui/lei', personBn: 'সে', form: 'fa', formBn: 'করে' },
            { person: 'noi', personBn: 'আমরা', form: 'facciamo', formBn: 'করি' },
            { person: 'voi', personBn: 'তোমরা', form: 'fate', formBn: 'করো' },
            { person: 'loro', personBn: 'তারা', form: 'fanno', formBn: 'করে' }
          ]
        },
        {
          id: '4',
          infinitive: 'Andare',
          infinitiveBn: 'যাওয়া',
          english: 'to go',
          conjugations: [
            { person: 'io', personBn: 'আমি', form: 'vado', formBn: 'যাই' },
            { person: 'tu', personBn: 'তুমি', form: 'vai', formBn: 'যাও' },
            { person: 'lui/lei', personBn: 'সে', form: 'va', formBn: 'যায়' },
            { person: 'noi', personBn: 'আমরা', form: 'andiamo', formBn: 'যাই' },
            { person: 'voi', personBn: 'তোমরা', form: 'andate', formBn: 'যাও' },
            { person: 'loro', personBn: 'তারা', form: 'vanno', formBn: 'যায়' }
          ]
        },
        {
          id: '5',
          infinitive: 'Venire',
          infinitiveBn: 'আসা',
          english: 'to come',
          conjugations: [
            { person: 'io', personBn: 'আমি', form: 'vengo', formBn: 'আসি' },
            { person: 'tu', personBn: 'তুমি', form: 'vieni', formBn: 'আসো' },
            { person: 'lui/lei', personBn: 'সে', form: 'viene', formBn: 'আসে' },
            { person: 'noi', personBn: 'আমরা', form: 'veniamo', formBn: 'আসি' },
            { person: 'voi', personBn: 'তোমরা', form: 'venite', formBn: 'আসো' },
            { person: 'loro', personBn: 'তারা', form: 'vengono', formBn: 'আসে' }
          ]
        },
        {
          id: '6',
          infinitive: 'Dire',
          infinitiveBn: 'বলা',
          english: 'to say',
          conjugations: [
            { person: 'io', personBn: 'আমি', form: 'dico', formBn: 'বলি' },
            { person: 'tu', personBn: 'তুমি', form: 'dici', formBn: 'বলো' },
            { person: 'lui/lei', personBn: 'সে', form: 'dice', formBn: 'বলে' },
            { person: 'noi', personBn: 'আমরা', form: 'diciamo', formBn: 'বলি' },
            { person: 'voi', personBn: 'তোমরা', form: 'dite', formBn: 'বলো' },
            { person: 'loro', personBn: 'তারা', form: 'dicono', formBn: 'বলে' }
          ]
        },
        {
          id: '7',
          infinitive: 'Vedere',
          infinitiveBn: 'দেখা',
          english: 'to see',
          conjugations: [
            { person: 'io', personBn: 'আমি', form: 'vedo', formBn: 'দেখি' },
            { person: 'tu', personBn: 'তুমি', form: 'vedi', formBn: 'দেখো' },
            { person: 'lui/lei', personBn: 'সে', form: 'vede', formBn: 'দেখে' },
            { person: 'noi', personBn: 'আমরা', form: 'vediamo', formBn: 'দেখি' },
            { person: 'voi', personBn: 'তোমরা', form: 'vedete', formBn: 'দেখো' },
            { person: 'loro', personBn: 'তারা', form: 'vedono', formBn: 'দেখে' }
          ]
        },
        {
          id: '8',
          infinitive: 'Parlare',
          infinitiveBn: 'কথা বলা',
          english: 'to speak',
          conjugations: [
            { person: 'io', personBn: 'আমি', form: 'parlo', formBn: 'কথা বলি' },
            { person: 'tu', personBn: 'তুমি', form: 'parli', formBn: 'কথা বলো' },
            { person: 'lui/lei', personBn: 'সে', form: 'parla', formBn: 'কথা বলে' },
            { person: 'noi', personBn: 'আমরা', form: 'parliamo', formBn: 'কথা বলি' },
            { person: 'voi', personBn: 'তোমরা', form: 'parlate', formBn: 'কথা বলো' },
            { person: 'loro', personBn: 'তারা', form: 'parlano', formBn: 'কথা বলে' }
          ]
        },
        {
          id: '9',
          infinitive: 'Mangiare',
          infinitiveBn: 'খাওয়া',
          english: 'to eat',
          conjugations: [
            { person: 'io', personBn: 'আমি', form: 'mangio', formBn: 'খাই' },
            { person: 'tu', personBn: 'তুমি', form: 'mangi', formBn: 'খাও' },
            { person: 'lui/lei', personBn: 'সে', form: 'mangia', formBn: 'খায়' },
            { person: 'noi', personBn: 'আমরা', form: 'mangiamo', formBn: 'খাই' },
            { person: 'voi', personBn: 'তোমরা', form: 'mangiate', formBn: 'খাও' },
            { person: 'loro', personBn: 'তারা', form: 'mangiano', formBn: 'খায়' }
          ]
        },
        {
          id: '10',
          infinitive: 'Bere',
          infinitiveBn: 'পান করা',
          english: 'to drink',
          conjugations: [
            { person: 'io', personBn: 'আমি', form: 'bevo', formBn: 'পান করি' },
            { person: 'tu', personBn: 'তুমি', form: 'bevi', formBn: 'পান করো' },
            { person: 'lui/lei', personBn: 'সে', form: 'beve', formBn: 'পান করে' },
            { person: 'noi', personBn: 'আমরা', form: 'beviamo', formBn: 'পান করি' },
            { person: 'voi', personBn: 'তোমরা', form: 'bevete', formBn: 'পান করো' },
            { person: 'loro', personBn: 'তারা', form: 'bevono', formBn: 'পান করে' }
          ]
        },
        {
          id: '11',
          infinitive: 'Dormire',
          infinitiveBn: 'ঘুমানো',
          english: 'to sleep',
          conjugations: [
            { person: 'io', personBn: 'আমি', form: 'dormo', formBn: 'ঘুমাই' },
            { person: 'tu', personBn: 'তুমি', form: 'dormi', formBn: 'ঘুমাও' },
            { person: 'lui/lei', personBn: 'সে', form: 'dorme', formBn: 'ঘুমায়' },
            { person: 'noi', personBn: 'আমরা', form: 'dormiamo', formBn: 'ঘুমাই' },
            { person: 'voi', personBn: 'তোমরা', form: 'dormite', formBn: 'ঘুমাও' },
            { person: 'loro', personBn: 'তারা', form: 'dormono', formBn: 'ঘুমায়' }
          ]
        },
        {
          id: '12',
          infinitive: 'Studiare',
          infinitiveBn: 'পড়াশোনা করা',
          english: 'to study',
          conjugations: [
            { person: 'io', personBn: 'আমি', form: 'studio', formBn: 'পড়াশোনা করি' },
            { person: 'tu', personBn: 'তুমি', form: 'studi', formBn: 'পড়াশোনা করো' },
            { person: 'lui/lei', personBn: 'সে', form: 'studia', formBn: 'পড়াশোনা করে' },
            { person: 'noi', personBn: 'আমরা', form: 'studiamo', formBn: 'পড়াশোনা করি' },
            { person: 'voi', personBn: 'তোমরা', form: 'studiate', formBn: 'পড়াশোনা করো' },
            { person: 'loro', personBn: 'তারা', form: 'studiano', formBn: 'পড়াশোনা করে' }
          ]
        }
      ]
    }
  };

  const getTitle = () => {
    if (!type) return '';
    if (topicTitle) {
      if (currentLanguage === 'it') return topicTitle.title_it;
      if (currentLanguage === 'bn') return topicTitle.title_bn;
      return topicTitle.title_en;
    }
    switch(type) {
      case 'verb-tenses': return content[currentLanguage].verbTenses;
      case 'articles': return content[currentLanguage].articles;
      case 'pronouns': return content[currentLanguage].pronouns;
      case 'prepositions': return content[currentLanguage].prepositions;
      case 'adjectives': return content[currentLanguage].adjectives;
      case 'questions': return content[currentLanguage].questions;
      case 'negation': return content[currentLanguage].negation;
      case 'verbs': return content[currentLanguage].verbs;
      default: return type;
    }
  };

  const getGrammarData = () => {
    if (!type) return { rules: [] as { italian: string; bengali: string }[], examples: [] };
    if (fetchedData) return fetchedData;
    return grammarData[type] || { rules: [] as { italian: string; bengali: string }[], examples: [] };
  };

  const title = getTitle();
  const { rules, examples } = getGrammarData();

  // Speak category name when screen loads (only once per navigation)
  useEffect(() => {
    if (!type) return;
    
    // Check if we've already spoken for this type
    const lastSpokenType = sessionStorage.getItem('lastSpokenGrammarType');
    const lastSpokenTime = sessionStorage.getItem('lastSpokenGrammarTime');
    const now = Date.now();
    
    // Only speak if it's a different type or more than 1 second has passed
    if (lastSpokenType === type && lastSpokenTime && (now - parseInt(lastSpokenTime)) < 1000) {
      return;
    }
    
    const speakCategoryName = async () => {
      try {
        const categoryNames: { [key: string]: string } = {
          'verb-tenses': 'Tempi Verbali',
          'articles': 'Articoli',
          'pronouns': 'Pronomi',
          'prepositions': 'Preposizioni',
          'adjectives': 'Aggettivi',
          'questions': 'Domande',
          'negation': 'Negazione',
          'verbs': 'Verbi'
        };
        const italianName = topicTitle?.title_it || categoryNames[type || ''] || '';
        if (italianName) {
          // Mark as spoken
          sessionStorage.setItem('lastSpokenGrammarType', type);
          sessionStorage.setItem('lastSpokenGrammarTime', now.toString());
          
          // Small delay to ensure audio is ready
          await new Promise(resolve => setTimeout(resolve, 500));
          await speakText(italianName, 'answer');
        }
      } catch (error) {
        console.error('Error speaking category:', error);
      }
    };

    speakCategoryName();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, topicTitle]);

  const handleExampleClick = async (example: GrammarExample) => {
    setSelectedExample(example.id);
    
    try {
      await speakText(example.italian, 'answer');
    } catch (error) {
      console.error('Error speaking:', error);
    }

    setTimeout(() => {
      setSelectedExample(null);
    }, 500);
  };

  const handleVerbConjugationClick = async (verbId: string, conjIndex: number, textToSpeak: string) => {
    setSelectedExample(`${verbId}-${conjIndex}`);
    
    try {
      await speakText(textToSpeak, 'answer');
    } catch (error) {
      console.error('Error speaking:', error);
    }

    setTimeout(() => {
      setSelectedExample(null);
    }, 500);
  };

  return (
    <div className="grammar-detail-screen">
      {/* Header */}
      <div className="screen-header">
        <button 
          className="header-btn back-btn"
          onClick={() => navigate(-1)}
          aria-label="Go back"
        >
          ←
        </button>
        <div className="header-center">
          <h1>{title}</h1>
        </div>
        <button 
          className="header-btn settings-btn"
          onClick={() => navigate('/settings')}
          aria-label="Open settings"
        >
          ⋯
        </button>
      </div>

      <LessonBannerAd />

      {/* Grammar Content */}
      <div className="grammar-container">
        {/* Rules Section */}
        {rules.length > 0 && (
          <div className="grammar-rules-section">
            <h3>{content[currentLanguage].rules}</h3>
            <div className="rules-list">
              {rules.map((rule, index) => (
                <div key={index} className="rule-item">
                  <span className="rule-number">{index + 1}.</span>
                  <div className="rule-content">
                    <span className="rule-text-italian">{rule.italian}</span>
                    <span className="rule-text-bengali">{rule.bengali}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Examples Section */}
        {examples.length > 0 && type !== 'verbs' && (
          <div className="grammar-examples-section">
            <h3>{content[currentLanguage].examples}</h3>
            <div className="examples-grid">
              {(examples as GrammarExample[]).map((example) => (
                <div
                  key={example.id}
                  className={`example-item ${selectedExample === example.id ? 'selected' : ''}`}
                  onClick={() => handleExampleClick(example)}
                >
                  <div className="example-bilingual">
                    <span className="example-text-italian">{example.italian}</span>
                    <span className="example-text-bengali">{example.bengali}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Verbs Conjugations Section */}
        {type === 'verbs' && examples.length > 0 && (
          <div className="grammar-verbs-section">
            <h3>{content[currentLanguage].examples}</h3>
            <div className="verbs-list">
              {(examples as VerbData[]).map((verb) => (
                <div key={verb.id} className="verb-card">
                  <h4 className="verb-infinitive">
                    <span className="verb-infinitive-it">{verb.infinitive}</span>
                    <span className="verb-infinitive-bn">({verb.infinitiveBn})</span>
                  </h4>
                  <div className="verb-conjugations">
                    {verb.conjugations.map((conj, index) => (
                      <div
                        key={index}
                        className={`verb-conjugation-item ${selectedExample === `${verb.id}-${index}` ? 'selected' : ''}`}
                        onClick={() => handleVerbConjugationClick(verb.id, index, `${conj.person} ${conj.form}`)}
                      >
                        <div className="conjugation-line">
                          <span className="conjugation-italian">{conj.person} {conj.form}</span>
                        </div>
                        <div className="conjugation-line">
                          <span className="conjugation-bengali">{conj.personBn} {conj.formBn}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
};

export default GrammarDetailScreen;

